import { useRef, useEffect, useCallback } from "react";
import { Volume2 } from "lucide-react";
import { LANG_FLAGS as FLAGS, LANG_NAMES } from "@/config/languages";

export default function TranslationPanel({ segments, targetLang, partialTranslation, onPlayTts }) {
  const scrollRef = useRef(null);
  const autoScroll = useRef(true);

  useEffect(() => {
    if (autoScroll.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [segments, partialTranslation]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    autoScroll.current = scrollHeight - scrollTop - clientHeight < 40;
  };

  if (targetLang === "off") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-gray-600" />
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Translation</h3>
        </div>
        <div className="flex items-center justify-center h-full text-gray-600 text-sm">
          <p>Select a target language to enable translation</p>
        </div>
      </div>
    );
  }

  const langName = LANG_NAMES[targetLang] || targetLang;
  const flag = FLAGS[targetLang] || "🌐";

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-amber-400" />
        <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Translation</h3>
        <span className="text-[10px] text-amber-400 ml-auto">{flag} {langName.toUpperCase()}</span>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto space-y-1 pr-1 scrollbar-thin scrollbar-thumb-gray-700"
        style={{ maxHeight: "calc(100vh - 280px)", minHeight: 200 }}
      >
        {segments.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            <p>Waiting for transcription...</p>
          </div>
        )}

        {segments.map((seg, i) => (
          <div key={seg.id || i} className="border-l-2 border-l-amber-600/50 pl-3 py-1 hover:bg-gray-800/30 transition-colors rounded-r">
            {seg.speaker && (
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] font-semibold text-amber-400/70 uppercase">{seg.speaker}</span>
              </div>
            )}
            <div className="flex items-start gap-1">
              <p className={`text-sm leading-relaxed flex-1 ${seg.is_final ? "text-gray-100" : "text-amber-300/70 italic"}`}>
                {seg.translation || "(translating...)"}
              </p>
              {seg.translation && onPlayTts && (
                <button
                  onClick={(e) => { e.stopPropagation(); onPlayTts(seg.translation, targetLang); }}
                  className="opacity-0 group-hover:opacity-100 text-amber-500 hover:text-amber-300 transition-all shrink-0 mt-0.5"
                  title="Read aloud"
                >
                  <Volume2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}

        {partialTranslation && (
          <div className="border-l-2 border-l-gray-600 pl-3 py-1 animate-pulse">
            <p className="text-sm text-gray-400 italic">▌{partialTranslation}</p>
          </div>
        )}
      </div>
    </div>
  );
}
